// src/components/AuthorProfile.tsx

"use client";

import Image from "next/image";
import Link from "next/link";
import { Author } from "@/types/common";
import GumletImage from "@/utils/GumletSrcSet";
import { useEffect, useRef, useState } from "react";

interface AuthorProfileProps {
  author: Author;
}

const AuthorProfile = ({ author }: AuthorProfileProps) => {
  const {
    firstName,
    lastName,
    position,
    company,
    email,
    profileImage,
    linkedin,
    name,
    bio,
  } = author;

  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isBioClamped, setIsBioClamped] = useState(false);
  const bioRef = useRef<HTMLParagraphElement>(null);

  // Most authors have no bio, and the ones that do vary from a line to a
  // paragraph. Only offer "Read more" when the text actually overflows the
  // clamp. Skip measuring while expanded, where scrollHeight === clientHeight.
  useEffect(() => {
    const element = bioRef.current;
    if (!bio || isBioExpanded || !element) return;

    const measure = () =>
      setIsBioClamped(element.scrollHeight > element.clientHeight + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [bio, isBioExpanded]);

  // Helper function to ensure LinkedIn URL has proper protocol
  const formatLinkedInUrl = (url: string) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  };

  return (
    <div className="flex items-start gap-6 mb-8">
      <div className="flex-1">
        <div className="flex items-center justify-between">
        <h1 className="text-5xl md:text-7xl font-medium">
          {name}
          <span className="text-orange">.</span>
        </h1>
        <div className="relative w-20 h-20 rounded-full flex-shrink-0 overflow-hidden md:hidden">
            <GumletImage
              imageUrl={profileImage}
              fallbackSrc="/assets/images/profile.svg"
              alt={`${firstName} ${lastName}`}
              fill
              className="object-cover"
              loading="lazy"
            />
        </div>
        </div>

        <div className="flex flex-wrap gap-y-2 gap-x-6 mt-7 justify-start items-end">
          {position && (
            <div>
              <p className="text-xs font-semibold text-inkBlack/40">Position</p>
              <p className="text-inkBlack">{position}</p>
            </div>
          )}
          {company && (
            <div>
              <p className="text-xs font-semibold text-inkBlack/40">Company</p>
              <p className="text-inkBlack">{company}</p>
            </div>
          )}
          {email && (
            <div>
              <p className="text-xs font-semibold text-inkBlack/40">Email</p>
              <p className="text-inkBlack">{email}</p>
            </div>
          )}
          {linkedin && linkedin !== "null" && (
            <div className="flex items-baseline pb-2">
              <Link
                href={formatLinkedInUrl(linkedin)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-inactiveGray relative hover:text-blue-600"
                prefetch={false}
              >
                <div className="relative h-4 w-4">
                  <Image src="/assets/linkedIn.svg" alt="LinkedIn" fill loading="lazy" />
                </div>
              </Link>
            </div>
          )}
        </div>

        {bio && (
          <div className="mt-7">
            <p className="text-xs font-semibold text-inkBlack/40">About</p>
            <p
              ref={bioRef}
              className={`text-inkBlack mt-1 text-sm leading-relaxed ${
                isBioExpanded ? "" : "line-clamp-3"
              }`}
            >
              {bio}
            </p>
            {(isBioClamped || isBioExpanded) && (
              <button
                type="button"
                onClick={() => setIsBioExpanded((expanded) => !expanded)}
                className="mt-1 text-xs font-bold text-orange hover:underline"
                aria-expanded={isBioExpanded}
              >
                {isBioExpanded ? "READ LESS" : "READ MORE"}
              </button>
            )}
          </div>
        )}
      </div>
      <div className="md:w-1/6 md:block hidden">
        <div className="relative w-20 h-20 md:w-32 md:h-32 rounded-full overflow-hidden">
          {/* {profileImage && ( */}
            <GumletImage
              imageUrl={profileImage}
              fallbackSrc="/assets/images/profile.svg"
              alt={`${firstName} ${lastName}`}
              fill
              className="object-cover"
              loading="lazy"
            />
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default AuthorProfile;
